import React, { useEffect, useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";

/** The narrative-only workflow is budgeted at ~3 minutes. */
export const NARRATIVE_COUNTDOWN_MS = 3 * 60_000;

/**
 * Shown in place of the countdown once the budgeted window drains. A run can
 * legitimately overrun the budget, so the copy has to reassure rather than
 * imply something went wrong.
 */
export const GENERATION_OVERTIME_MESSAGE =
  "This is taking a little longer than usual — your report will be ready in a few moments.";

const toMs = (value: Date | string | number): number => {
  const t = new Date(value).getTime();
  return Number.isNaN(t) ? Date.now() : t;
};

const formatRemaining = (ms: number): string => {
  const totalSec = Math.ceil(ms / 1000);
  const minutes = Math.floor(totalSec / 60);
  const seconds = totalSec % 60;
  return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
};

/**
 * Time-driven progress for narrative-only generation, which has no meaningful
 * server-side percentage. Counts a fixed window down to 0:00 while the ring/bar
 * fill with elapsed time. Once the window drains the countdown is spent
 * (`expired`) — callers drop the mm:ss readout and switch to an indeterminate
 * "taking longer than usual" state until the job actually completes.
 */
export const useGenerationCountdown = (
  startedAt: Date | string | number,
  durationMs: number = NARRATIVE_COUNTDOWN_MS,
  enabled: boolean = true,
) => {
  const anchor = toMs(startedAt);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!enabled) return;
    setNow(Date.now());
    const id = window.setInterval(() => {
      const tick = Date.now();
      setNow(tick);
      // Past the window there is nothing left to count — the overtime state is
      // static, so stop ticking instead of re-rendering once a second forever.
      if (tick - anchor >= durationMs) window.clearInterval(id);
    }, 1000);
    return () => window.clearInterval(id);
  }, [anchor, durationMs, enabled]);

  const elapsed = Math.max(0, now - anchor);
  const remainingMs = Math.max(0, durationMs - elapsed);
  const elapsedPct =
    durationMs > 0 ? Math.min(100, (elapsed / durationMs) * 100) : 0;

  return {
    remainingMs,
    elapsedPct,
    label: formatRemaining(remainingMs),
    expired: enabled && remainingMs <= 0,
  };
};

interface CountdownRingProps {
  /** Elapsed fraction (0–100) — fills the ring as the window drains. */
  value: number;
  /** mm:ss remaining, shown at the ring's center. */
  label: string;
  size: number;
  thickness: number;
  labelVariant?: "caption" | "subtitle1";
  /**
   * Window drained: hide the mm:ss readout and spin indeterminately so the
   * ring reads as "still working, no estimate" rather than a stalled 0:00.
   */
  expired?: boolean;
}

/** Circular progress with the remaining mm:ss readout at its center. */
export const CountdownRing: React.FC<CountdownRingProps> = ({
  value,
  label,
  size,
  thickness,
  labelVariant = "subtitle1",
  expired = false,
}) => (
  <Box sx={{ position: "relative", display: "inline-flex" }}>
    <CircularProgress
      variant={expired ? "indeterminate" : "determinate"}
      value={expired ? undefined : value}
      size={size}
      thickness={thickness}
      sx={{
        color: "primary.main",
        "& .MuiCircularProgress-circle": { strokeLinecap: "round" },
      }}
    />
    {!expired && (
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography
          variant={labelVariant}
          component="div"
          fontWeight="bold"
          color="primary"
        >
          {label}
        </Typography>
      </Box>
    )}
  </Box>
);
